import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { formatMoqLabel, getEffectiveMoq, isFactoryProductListing } from '../../util/factoryListing';
import { Heading } from '../../components';
import listingCss from './ListingPage.module.css';
import css from './SectionProductDetails.module.css';

const INDUSTRY_LABELS = {
  clothing: 'Clothing & Apparel',
  electronics: 'Electronics',
  furniture: 'Furniture',
  food: 'Food & Beverages',
  chemicals: 'Chemicals',
  Electronics: 'Electronics',
  Furniture: 'Furniture',
  Textile: 'Textile',
  Machinery: 'Machinery',
  Automotive: 'Automotive',
  Chemical: 'Chemical',
  'Food & Beverage': 'Food & Beverage',
  'Toys & Games': 'Toys & Games',
  'Plastic & Rubber': 'Plastic & Rubber',
  'Metal & Steel': 'Metal & Steel',
};

const VerifiedIcon = () => (
  <svg className={css.verifiedIcon} width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M12 2l2.4 1.1 2.6-.2.9 2.4 2.4.9-.2 2.6L22 12l-1.1 2.4.2 2.6-2.4.9-.9 2.4-2.6-.2L12 22l-2.4-1.1-2.6.2-.9-2.4-2.4-.9.2-2.6L2 12l1.1-2.4-.2-2.6 2.4-.9.9-2.4 2.6.2L12 2zm-1.1 13.3l5.7-5.7-1.4-1.4-4.3 4.3-2.1-2.1-1.4 1.4 3.5 3.5z"
    />
  </svg>
);

const Field = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <div className={css.fieldCard}>
      <span className={css.fieldLabel}>{label}</span>
      <span className={css.fieldValue}>{value}</span>
    </div>
  );
};

const JOURNEY_STEPS = [
  'ListingPage.buyerJourney.stepView',
  'ListingPage.buyerJourney.stepAgent',
  'ListingPage.buyerJourney.stepQty',
  'ListingPage.buyerJourney.stepMoq',
];

/**
 * SectionProductDetails – factory product details with buyer journey UI.
 */
const SectionProductDetails = ({ publicData }) => {
  if (!publicData || !isFactoryProductListing(publicData)) return null;

  const {
    sku,
    category,
    industry,
    unitOfMeasurement,
    productionLeadTime,
    packagingInfo,
    qtyPerCarton,
    qtyPerPallet,
    qtyPerContainer,
    priceTiers,
    agentName,
  } = publicData;

  const hasProductData = sku || priceTiers?.length > 0 || productionLeadTime || packagingInfo;
  if (!hasProductData) return null;

  const sortedTiers = Array.isArray(priceTiers)
    ? priceTiers.slice().sort((a, b) => a.minQty - b.minQty)
    : [];

  const unit = unitOfMeasurement || 'pcs';
  const basePrice = sortedTiers[0]?.price ?? null;
  const moq = getEffectiveMoq(publicData);
  const moqLabel = formatMoqLabel(moq, unit);
  const industryLabel = INDUSTRY_LABELS[industry] || industry;

  return (
    <section className={css.root}>
      <div className={css.heroMeta}>
        <div className={css.badgeRow}>
          {category ? <span className={css.categoryBadge}>{category}</span> : null}
          {industryLabel ? <span className={css.industryBadge}>{industryLabel}</span> : null}
          <span className={css.verifiedBadge}>
            <VerifiedIcon />
            <FormattedMessage id="ListingPage.verifiedProduct" />
          </span>
        </div>
        <div className={css.statRow}>
          {moqLabel ? (
            <div className={css.statPill}>
              <span className={css.statLabel}>
                <FormattedMessage id="ListingPage.statMoq" />
              </span>
              <span className={css.statValue}>{moqLabel}</span>
            </div>
          ) : null}
          {productionLeadTime ? (
            <div className={css.statPill}>
              <span className={css.statLabel}>
                <FormattedMessage id="ListingPage.statLeadTime" />
              </span>
              <span className={css.statValue}>{productionLeadTime}</span>
            </div>
          ) : null}
          {unitOfMeasurement ? (
            <div className={css.statPill}>
              <span className={css.statLabel}>
                <FormattedMessage id="ListingPage.statUnit" />
              </span>
              <span className={css.statValue}>{unitOfMeasurement}</span>
            </div>
          ) : null}
          {sku ? (
            <div className={css.statPill}>
              <span className={css.statLabel}>SKU</span>
              <span className={css.statValue}>{sku}</span>
            </div>
          ) : null}
        </div>
      </div>

      <div className={css.journeyBanner}>
        <p className={css.journeyBannerTitle}>
          <FormattedMessage id="ListingPage.buyerJourneyTitle" />
        </p>
        <ol className={css.journeySteps}>
          {JOURNEY_STEPS.map((id, index) => (
            <li key={id} className={css.journeyStep}>
              <span className={css.stepNum}>{index + 1}</span>
              <span className={css.stepText}>
                <FormattedMessage id={id} />
              </span>
            </li>
          ))}
        </ol>
      </div>

      <Heading as="h2" rootClassName={listingCss.sectionHeadingWithExtraMargin}>
        <FormattedMessage id="ListingPage.productDetailsTitle" />
      </Heading>
      <div className={css.grid}>
        <Field label="SKU" value={sku} />
        <Field label="Category" value={category} />
        <Field label="Industry" value={industryLabel} />
        <Field label="Assigned sourcing agent" value={agentName} />
        <Field label="MOQ" value={moqLabel} />
        <Field label="Unit" value={unitOfMeasurement} />
        <Field label="Production Lead Time" value={productionLeadTime} />
        <Field label="Packaging" value={packagingInfo} />
        <Field label="Qty / Carton" value={qtyPerCarton ? `${qtyPerCarton} ${unit}` : null} />
        <Field label="Qty / Pallet" value={qtyPerPallet ? `${qtyPerPallet} ${unit}` : null} />
        <Field
          label="Qty / Container"
          value={qtyPerContainer ? `${qtyPerContainer} ${unit}` : null}
        />
      </div>

      {sortedTiers.length > 0 ? (
        <div className={css.tiersSection}>
          <Heading as="h2" rootClassName={listingCss.sectionHeadingWithExtraMargin}>
            <FormattedMessage id="ListingPage.volumePricingTitle" />
          </Heading>
          <p className={css.tiersSub}>
            <FormattedMessage id="ListingPage.volumePricingSubtitle" />
          </p>
          <div className={css.tiersTable}>
            <div className={css.tiersHead}>
              <span>
                <FormattedMessage id="ListingPage.tierMinQty" />
              </span>
              <span>
                <FormattedMessage id="ListingPage.tierPrice" />
              </span>
              <span>
                <FormattedMessage id="ListingPage.tierSaving" />
              </span>
            </div>
            {sortedTiers.map((tier, idx) => {
              const saving =
                basePrice && basePrice > 0 && idx > 0
                  ? (((basePrice - tier.price) / basePrice) * 100).toFixed(1)
                  : null;
              return (
                <div key={idx} className={css.tierRow}>
                  <span className={css.tierQty}>
                    {tier.minQty.toLocaleString()} {unit}
                  </span>
                  <span className={css.tierPrice}>${tier.price}</span>
                  <span className={css.tierSaving}>
                    {saving && Number(saving) > 0 ? (
                      <span className={css.savingBadge}>−{saving}%</span>
                    ) : idx === 0 ? (
                      <span className={css.baseBadge}>Base</span>
                    ) : (
                      '—'
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default SectionProductDetails;
